// ─────────────────────────────────────────────────────────────────────────────
// V2Home — the v2 dashboard.
// Data: the same single endpoint as v1 (GET /api/dashboard/home-summary) and the
// same 60s activity refresh (GET /api/activity). Record-payment posts the same
// body to POST /api/invoices/payment/:id. Business formulas copied from v1.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, TINTS, WidgetIcon } from "./icons";
import { Money, MenuButton, Segmented, Ring, Sheet, Switch, Skel, inr, inrCompact, useMedia } from "./primitives";
import { useCreateActions } from "./V2Shell";
import { AgingBar } from "./V2Reports";
import { useBrief, remindClient, daysUntil } from "./dashboardBrief";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Validated categorical palette (dataviz skill reference instance) — fixed order.
const SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const daysSince = (d) => Math.ceil(Math.abs(Date.now() - new Date(d)) / 86400000);

const relTime = (d) => {
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return Math.floor(h / 24) === 1 ? "Yesterday" : new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const pctChange = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : null);

// Stable tint per name for avatars.
const TINT_KEYS = ["blue", "indigo", "teal", "orange", "purple", "pink", "green", "cyan"];
const tintFor = (s = "") => TINT_KEYS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % TINT_KEYS.length];

function Avatar({ name, size = 38 }) {
  const [top, bottom] = TINTS[tintFor(name)];
  const ini = (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <span className="v2-avatar v2-avatar--name" style={{ width: size, height: size, fontSize: size * 0.36, "--top": top, "--tint": bottom }}>
      {ini}
    </span>
  );
}

// Status pill: icon + label, never colour alone.
function Status({ tone, children }) {
  const glyph = tone === "good" ? "check" : tone === "warn" ? "clock" : "alert";
  return (
    <span className={`v2-status v2-status--${tone}`}>
      <Glyph name={glyph} size={13} stroke={2.2} /> {children}
    </span>
  );
}

function Delta({ value, label, invert = false }) {
  if (value === null || value === undefined || !isFinite(value)) return <span className="v2-delta v2-delta--flat">{label}</span>;
  const up = value >= 0;
  const good = invert ? !up : up;
  return (
    <span className={`v2-delta ${good ? "v2-delta--good" : "v2-delta--bad"}`}>
      <span className="v2-delta-num">{up ? "+" : "−"}{Math.abs(value).toFixed(0)}%</span> {label}
    </span>
  );
}

// line-glyph name → duotone icon name
const DUO_FOR = { inbox: "receivables", cart: "payables" };

function CardHead({ glyph, duo, tint, title, menu, extra }) {
  return (
    <div className="v2-card-head">
      {glyph && <WidgetIcon name={duo || DUO_FOR[glyph] || glyph} glyph={glyph} tint={tint} size={32} />}
      <h3 className="v2-card-title">{title}</h3>
      {extra}
      {menu && <MenuButton items={menu} className="v2-icon-btn v2-icon-btn--soft" label={`${title} options`} />}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="v2-tooltip">
      <div className="v2-tooltip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="v2-tooltip-row">
          <span className="v2-key" style={{ background: p.color }} />
          <span>{p.name}</span>
          <strong>{inr(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

// ── Widgets: order + visibility are user-customisable (saved per browser) ───────
const WIDGETS = [
  { id: "kpis", label: "Key figures" },
  { id: "dues", label: "Payments due" },
  { id: "chart", label: "Revenue vs expenses" },
  { id: "cash", label: "Cashflow" },
  { id: "todo", label: "To do & deadlines", isNew: true },
  { id: "gst", label: "GST for last month", isNew: true },
  { id: "pulse", label: "Business pulse" },
  { id: "breakdown", label: "Where money goes" },
  { id: "payroll", label: "Payroll" },
  { id: "attn", label: "Needs attention" },
  { id: "activity", label: "Recent activity" },
  { id: "owed", label: "Who owes you", isNew: true },
];
const KPI_TILES = [
  { id: "revenue", label: "Revenue" }, { id: "expenses", label: "Expenses" }, { id: "profit", label: "Net profit" },
  { id: "receivables", label: "Receivables" }, { id: "payables", label: "Payables" }, { id: "gstCollected", label: "GST collected" },
];
const LAYOUT_KEY = "eb-v2-home-layout";
const DEFAULT_LAYOUT = { order: WIDGETS.map((w) => w.id), hidden: ["owed"], kpiHidden: [] };
const loadLayout = () => {
  try {
    const s = JSON.parse(localStorage.getItem(LAYOUT_KEY));
    if (s && Array.isArray(s.order)) {
      const ids = WIDGETS.map((w) => w.id);
      const order = s.order.filter((id) => ids.includes(id));
      ids.forEach((id) => { if (!order.includes(id)) order.splice(Math.min(ids.indexOf(id), order.length), 0, id); });
      return { order, hidden: (s.hidden || []).filter((id) => ids.includes(id)), kpiHidden: (s.kpiHidden || []).filter((id) => KPI_TILES.some((t) => t.id === id)) };
    }
  } catch { /* ignore */ }
  return DEFAULT_LAYOUT;
};

// ══════════════════════════════════════════════════════════════════════════════
export default function V2Home({ showAlert }) {
  usePageTitle("Dashboard");
  const navigate = useNavigate();
  const { user, hasPermission } = useContext(userContext);
  const isPhone = useMedia("(max-width: 640px)");
  const now = new Date();
  const createActions = useCreateActions();

  const [mode, setMode] = useState("monthly");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("suppliers");
  const [payFor, setPayFor] = useState(null);
  const payOrigin = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/dashboard/home-summary?month=${month}&year=${year}&mode=${mode}`,
        { headers: { "auth-token": localStorage.getItem("token") } }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Failed to load dashboard");
      setData(d);
    } catch (e) {
      setError(e.message || "Couldn't load the dashboard.");
    } finally {
      setLoading(false);
    }
  }, [month, year, mode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Same silent 60s activity refresh as v1.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/activity`, { headers: { "auth-token": localStorage.getItem("token") } });
        const d = await r.json();
        setData((prev) => (prev ? { ...prev, activity: d.activities || [] } : prev));
      } catch { /* silent */ }
    }, 60000);
    return () => clearInterval(t);
  }, []);

  // ── Period stepping (never into the future) ────────────────────────────────
  const atLatest = mode === "monthly"
    ? year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1)
    : year >= now.getFullYear();
  const step = (dir) => {
    if (dir > 0 && atLatest) return;
    if (mode === "yearly") { setYear((y) => y + dir); return; }
    const d = new Date(year, month - 1 + dir, 1);
    setMonth(d.getMonth() + 1);
    setYear(d.getFullYear());
  };
  const periodLabel = mode === "monthly" ? `${isPhone ? MONTHS[month - 1].slice(0, 3) : MONTHS[month - 1]} ${year}` : `${year}`;

  // ── Derived values (formulas mirror v1) ────────────────────────────────────
  const k = data?.kpi || {};
  const cf = data?.cashflow || { cashIn: 0, billsPaid: 0, payrollPaid: 0, purchasesPaid: 0 };
  const receivables = k.receivables || { amount: 0, count: 0 };
  const payables = k.payables || { purchasesAmount: 0, billsAmount: 0, total: 0 };
  const trend = data?.trend || [];
  const cur = trend[trend.length - 1];
  const prev = trend[trend.length - 2];
  const revDelta = mode === "monthly" && cur && prev ? pctChange(cur.revenue, prev.revenue) : null;
  const expDelta = mode === "monthly" && cur && prev ? pctChange(cur.expenses, prev.expenses) : null;
  const prevLabel = prev ? `vs ${prev.label}` : "";

  const totalOut = cf.billsPaid + cf.payrollPaid + cf.purchasesPaid;
  const net = cf.cashIn - totalOut;
  const collectionEff = Math.min(Math.round((cf.cashIn / (receivables.amount || 1)) * 100), 100);

  const daysElapsed = Math.max(now.getDate(), 1);
  const dailyBurn = totalOut / daysElapsed;
  const pulseCollEff = Math.min(Math.round((((k.revenue || 0) - (receivables.amount || 0)) / (k.revenue || 1)) * 100), 100);
  const payablesCoverage = Math.min(Math.round((cf.cashIn / (payables.total || 1)) * 100), 200);
  const outstandingRatio = Math.min(Math.round((receivables.amount / (k.revenue || 1)) * 100), 200);
  const tone3 = (v, g, a) => (v >= g ? "good" : v >= a ? "warn" : "bad");

  const pulse = [
    { label: "Daily burn", note: `${daysElapsed} days elapsed`, value: `${inr(dailyBurn)}/day`, tone: null },
    { label: "Collection efficiency", note: "Billed vs still owed", value: `${Math.max(pulseCollEff, 0)}%`, tone: tone3(pulseCollEff, 70, 40), toneLabel: pulseCollEff >= 70 ? "Healthy" : pulseCollEff >= 40 ? "Moderate" : "Low" },
    { label: "Avg invoice value", note: `${receivables.count} invoices`, value: receivables.count > 0 ? inr((k.revenue || 0) / Math.max(receivables.count, 1)) : "—", tone: null },
    { label: "Payables coverage", note: "Cash in ÷ payables", value: `${payablesCoverage}%`, tone: tone3(payablesCoverage, 100, 60), toneLabel: payablesCoverage >= 100 ? "Covered" : payablesCoverage >= 60 ? "Partial" : "Short" },
    { label: "Outstanding ratio", note: "Owed ÷ revenue", value: `${outstandingRatio}%`, tone: outstandingRatio < 30 ? "good" : outstandingRatio < 60 ? "warn" : "bad", toneLabel: outstandingRatio < 30 ? "Low" : outstandingRatio < 60 ? "Medium" : "High" },
  ];

  // Expense breakdown: top 5 + "Other" (never generate a 9th hue).
  const breakdown = useMemo(() => {
    const list = [...(data?.expenseBreakdown || [])].sort((a, b) => b.amount - a.amount);
    const top = list.slice(0, 5);
    const rest = list.slice(5).reduce((s, e) => s + (e.amount || 0), 0);
    const out = rest > 0 ? [...top, { category: "Other", amount: rest }] : top;
    const total = out.reduce((s, e) => s + (e.amount || 0), 0);
    return { items: out.map((e, i) => ({ ...e, color: SERIES[i], pct: total ? (e.amount / total) * 100 : 0 })), total };
  }, [data]);

  const payroll = data?.payroll || { paid: 0, total: 0, paidAmount: 0, pendingAmount: 0 };
  const clientDues = data?.clientDues || [];
  const supplierDues = data?.supplierDues || [];
  const lowStock = data?.lowStock || [];
  const recurring = data?.upcomingRecurring || [];
  const activity = data?.activity || [];
  const first = !data && loading;

  // Added: brief (to do, GST, who owes you), customisable layout, early-month notes
  const brief = useBrief({ user, hasPermission, summary: data });
  const [layout, setLayout] = useState(loadLayout);
  const [customising, setCustomising] = useState(false);
  const [reminding, setReminding] = useState("");
  useEffect(() => { try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)); } catch { /* ignore */ } }, [layout]);
  const kpiOn = (id) => !layout.kpiHidden.includes(id);
  const isCurMonth = mode === "monthly" && month === now.getMonth() + 1 && year === now.getFullYear();
  const early = isCurMonth && now.getDate() <= 10 && !!prev;     // a few days in: % vs last month is misleading
  const dim = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const earlyNote = (v) => <span className="v2-kpi-note">Day {now.getDate()} of {dim} · {prev?.label} {inrCompact(v)}</span>;
  const goWidget = (id) => {
    if (layout.hidden.includes(id)) setLayout((l) => ({ ...l, hidden: l.hidden.filter((x) => x !== id) }));
    setTimeout(() => document.getElementById(`v2h-w-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };
  const remind = async (c) => {
    setReminding(c.name);
    try { await remindClient(c, { onError: (m) => showAlert?.(m, "danger") }); }
    catch (e) { showAlert?.(e.message || "Couldn't prepare the reminder", "danger"); }
    finally { setReminding(""); }
  };

  const firstName = (user?.name || "").split(" ")[0];
  const company = user?.companyName;

  const openPay = (inv, e) => { payOrigin.current = e.currentTarget; setPayFor(inv); };

  // Every widget, keyed by id; the grid renders them in the user's order.
  const W = {
    kpis: (
        <section id="v2h-w-kpis" className="v2h-kpis" aria-label="Key figures">
          {kpiOn("revenue") && (<article className="v2-card v2-kpi">
            <CardHead glyph="rupee" tint="blue" title="Revenue" menu={[
              { label: "Create invoice", glyph: "plus", to: "/dashboard/createinvoice" },
              { label: "View invoices", glyph: "invoice", to: "/dashboard/viewinvoices" },
              { label: "Financial reports", glyph: "pie", to: "/dashboard/financial-reports" },
            ]} />
            {first ? <Skel h={34} w="75%" /> : <div className="v2-kpi-value"><Money value={k.revenue} /></div>}
            <div className="v2-kpi-foot">
              {k.creditNotesTotal > 0
                ? <span className="v2-kpi-note">−{inr(k.creditNotesTotal)} credit notes · <strong>{inr(k.netRevenue)}</strong> net</span>
                : early ? earlyNote(prev.revenue) : <Delta value={revDelta} label={revDelta === null ? (mode === "monthly" ? "This month" : "This year") : prevLabel} />}
            </div>
          </article>)}

          {kpiOn("expenses") && (<article className="v2-card v2-kpi">
            <CardHead glyph="wallet" tint="orange" title="Expenses" menu={[
              { label: "Add expense", glyph: "plus", to: "/dashboard/expenses" },
              { label: "Bills & expenses", glyph: "wallet", to: "/dashboard/bills" },
            ]} />
            {first ? <Skel h={34} w="70%" /> : <div className="v2-kpi-value"><Money value={k.totalExpenses} /></div>}
            <div className="v2-kpi-foot">
              {early ? earlyNote(prev.expenses) : <Delta value={expDelta} invert label={expDelta === null ? "All costs this period" : prevLabel} />}
            </div>
          </article>)}

          {kpiOn("profit") && (<article className="v2-card v2-kpi">
            <CardHead glyph="pulse" duo="profit" tint="green" title="Net profit" menu={[
              { label: "Profit & loss", glyph: "pie", to: "/dashboard/financial-reports" },
              { label: "Reports", glyph: "chart", to: "/dashboard/reports" },
            ]} />
            {first ? <Skel h={34} w="65%" /> : <div className="v2-kpi-value"><Money value={k.netProfit} /></div>}
            <div className="v2-kpi-foot">
              {!first && <Status tone={(k.netProfit || 0) >= 0 ? "good" : "bad"}>{(k.netProfit || 0) >= 0 ? "In profit" : "Running at a loss"}</Status>}
            </div>
          </article>)}

          {kpiOn("receivables") && (<article className="v2-card v2-kpi">
            <CardHead glyph="inbox" tint="indigo" title="Receivables" menu={[
              { label: "Client ledger", glyph: "ledger", to: "/dashboard/ledgerpage" },
              { label: "Invoices", glyph: "invoice", to: "/dashboard/viewinvoices" },
            ]} />
            {first ? <Skel h={34} w="70%" /> : <div className="v2-kpi-value"><Money value={receivables.amount} /></div>}
            <div className="v2-kpi-foot"><span className="v2-kpi-note">{receivables.count} invoice{receivables.count === 1 ? "" : "s"} pending</span></div>
          </article>)}

          {kpiOn("payables") && (<article className="v2-card v2-kpi">
            <CardHead glyph="cart" tint="pink" title="Payables" menu={[
              { label: "Purchase entry", glyph: "invoice", to: "/dashboard/purchase-entry" },
              { label: "Supplier ledger", glyph: "ledger", to: "/dashboard/supplier-ledger" },
            ]} />
            {first ? <Skel h={34} w="60%" /> : <div className="v2-kpi-value"><Money value={payables.total} /></div>}
            <div className="v2-kpi-foot"><span className="v2-kpi-note">Purchases {inrCompact(payables.purchasesAmount)} · Bills {inrCompact(payables.billsAmount)}</span></div>
          </article>)}

          {kpiOn("gstCollected") && (<article className="v2-card v2-kpi">
            <CardHead glyph="gst" tint="purple" title="GST collected" menu={[
              { label: "GST reports", glyph: "gst", to: "/dashboard/gst-reports" },
            ]} />
            {first ? <Skel h={34} w="60%" /> : <div className="v2-kpi-value"><Money value={k.gstCollected} /></div>}
            <div className="v2-kpi-foot"><span className="v2-kpi-note">Output tax {mode === "monthly" ? "this month" : "this year"}</span></div>
          </article>)}
        </section>
    ),
    dues: (
        <article id="v2h-w-dues" className="v2-card v2h-dues">
          <CardHead glyph="clock" tint="red" title="Payments due"
            extra={clientDues.length > 0 && <span className="v2-count">{clientDues.length}</span>}
            menu={[{ label: "Client ledger", glyph: "ledger", to: "/dashboard/ledgerpage" }, { label: "All invoices", glyph: "invoice", to: "/dashboard/viewinvoices" }]} />
          {first ? (
            <div className="v2-list">{[0, 1, 2, 3].map((i) => <Skel key={i} h={46} r={14} />)}</div>
          ) : clientDues.length === 0 ? (
            <div className="v2-empty v2-empty--good"><Glyph name="check" size={22} stroke={2} /> Nobody owes you right now.</div>
          ) : (
            <>
              <div className="v2-list v2-scroll">
                {clientDues.map((inv) => {
                  const days = daysSince(inv.invoiceDate);
                  const overdue = days > (inv.paymentTimeline || 30);
                  return (
                    <button key={inv._id} type="button" className="v2-row" onClick={(e) => openPay(inv, e)}
                      aria-label={`Record payment for ${inv.invoiceNumber}`}>
                      <Avatar name={inv.clientName} />
                      <span className="v2-row-main">
                        <span className="v2-row-title">{inv.clientName}</span>
                        <span className="v2-row-sub">{inv.invoiceNumber} · {days}d</span>
                      </span>
                      <span className="v2-row-end">
                        <span className="v2-row-amount">{inr((inv.grandTotal || 0) - (inv.amountPaid || 0))}</span>
                        {overdue ? <Status tone="bad">Overdue</Status> : <span className="v2-row-sub">Due in {(inv.paymentTimeline || 30) - days}d</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="v2-card-foot">
                Total <strong>{inr(clientDues.reduce((s, i) => s + ((i.grandTotal || 0) - (i.amountPaid || 0)), 0))}</strong> across {clientDues.length}
              </div>
            </>
          )}
        </article>
    ),
    chart: (
        <article id="v2h-w-chart" className="v2-card v2h-chart">
          <CardHead glyph="chart" tint="blue" title="Revenue vs expenses"
            extra={<span className="v2-card-caption">Last 6 months</span>}
            menu={[{ label: "Reports", glyph: "chart", to: "/dashboard/reports" }, { label: "Financial reports", glyph: "pie", to: "/dashboard/financial-reports" }]} />
          <div className="v2-legend">
            <span><i className="v2-key" style={{ background: SERIES[0] }} /> Revenue</span>
            <span><i className="v2-key" style={{ background: SERIES[1] }} /> Expenses</span>
          </div>
          {first ? <Skel h={220} r={16} /> : trend.length === 0 ? (
            <div className="v2-empty">No trend data yet.</div>
          ) : (
            <div className="v2-chart-box">
              <ResponsiveContainer width="100%" height={isPhone ? 200 : 240}>
                <BarChart data={trend} barGap={2} barCategoryGap="28%" margin={{ top: 8, right: 4, left: -6, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--v2-hair)" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "var(--v2-muted)" }} />
                  <YAxis axisLine={false} tickLine={false} width={52} tick={{ fontSize: 11, fill: "var(--v2-muted)" }} tickFormatter={inrCompact} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(120,130,150,0.08)", radius: 10 }} />
                  <Bar dataKey="revenue" name="Revenue" fill={SERIES[0]} radius={[4, 4, 0, 0]} maxBarSize={18} />
                  <Bar dataKey="expenses" name="Expenses" fill={SERIES[1]} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </article>
    ),
    cash: (
        <article id="v2h-w-cash" className="v2-card v2h-cash">
          <CardHead glyph="cash" tint="teal" title="Cashflow" />
          {first ? <Skel h={160} r={16} /> : (
            <>
              <div className="v2-cash-top">
                <div>
                  <div className="v2-label">Net cash</div>
                  <div className="v2-kpi-value v2-kpi-value--sm"><Money value={net} /></div>
                </div>
                <Ring value={collectionEff} size={76} stroke={8} label={`${collectionEff}% collected`}
                  color={collectionEff >= 70 ? "var(--v2-good)" : collectionEff >= 40 ? "var(--v2-warn)" : "var(--v2-bad)"}>
                  <strong>{collectionEff}%</strong><span>collected</span>
                </Ring>
              </div>
              {(() => {
                const max = Math.max(cf.cashIn, totalOut, 1);
                const outs = [
                  { label: "Bills", v: cf.billsPaid, c: SERIES[1] },
                  { label: "Payroll", v: cf.payrollPaid, c: SERIES[4] },
                  { label: "Purchases", v: cf.purchasesPaid, c: SERIES[6] },
                ];
                return (
                  <div className="v2-flow">
                    <div className="v2-flow-row">
                      <span className="v2-flow-label">In</span>
                      <div className="v2-flow-track"><span className="v2-flow-seg" style={{ width: `${(cf.cashIn / max) * 100}%`, background: SERIES[2] }} /></div>
                      <span className="v2-flow-val">{inrCompact(cf.cashIn)}</span>
                    </div>
                    <div className="v2-flow-row">
                      <span className="v2-flow-label">Out</span>
                      <div className="v2-flow-track">
                        {outs.filter((o) => o.v > 0).map((o) => (
                          <span key={o.label} className="v2-flow-seg" title={`${o.label}: ${inr(o.v)}`} style={{ width: `${(o.v / max) * 100}%`, background: o.c }} />
                        ))}
                      </div>
                      <span className="v2-flow-val">{inrCompact(totalOut)}</span>
                    </div>
                    <div className="v2-legend v2-legend--grid">
                      <span><i className="v2-key" style={{ background: SERIES[2] }} /> Cash in <b>{inr(cf.cashIn)}</b></span>
                      {outs.map((o) => <span key={o.label}><i className="v2-key" style={{ background: o.c }} /> {o.label} <b>{inr(o.v)}</b></span>)}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </article>
    ),
    pulse: (
        <article id="v2h-w-pulse" className="v2-card v2h-third">
          <CardHead glyph="pulse" tint="pink" title="Business pulse" />
          {first ? <div className="v2-list">{[0, 1, 2, 3, 4].map((i) => <Skel key={i} h={44} r={12} />)}</div> : (
            <div className="v2-inset v2-inset--flush">
              {pulse.map((p) => (
                <div key={p.label} className="v2-inset-row v2-inset-row--static">
                  <span className="v2-row-main">
                    <span className="v2-row-title">{p.label}</span>
                    <span className="v2-row-sub">{p.note}</span>
                  </span>
                  <span className="v2-row-end">
                    <span className="v2-row-amount">{p.value}</span>
                    {p.tone && <Status tone={p.tone}>{p.toneLabel}</Status>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </article>
    ),
    breakdown: (
        <article id="v2h-w-breakdown" className="v2-card v2h-third">
          <CardHead glyph="pie" tint="orange" title="Where money goes" menu={[{ label: "Expenses", glyph: "wallet", to: "/dashboard/expenses" }]} />
          {first ? <Skel h={200} r={16} /> : breakdown.items.length === 0 ? (
            <div className="v2-empty">No expenses recorded for this period.</div>
          ) : (
            <div className="v2-donut-wrap">
              <div className="v2-donut">
                <ResponsiveContainer width="100%" height={168}>
                  <PieChart>
                    <Pie data={breakdown.items} dataKey="amount" nameKey="category" innerRadius={56} outerRadius={78}
                      paddingAngle={2} stroke="var(--v2-card)" strokeWidth={2} cornerRadius={4} isAnimationActive={false}>
                      {breakdown.items.map((e) => <Cell key={e.category} fill={e.color} />)}
                    </Pie>
                    <Tooltip content={({ active, payload }) => active && payload?.length ? (
                      <div className="v2-tooltip"><div className="v2-tooltip-row"><span className="v2-key" style={{ background: payload[0].payload.color }} /><span>{payload[0].name}</span><strong>{inr(payload[0].value)}</strong></div></div>
                    ) : null} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="v2-donut-center"><span>Total</span><strong>{inrCompact(breakdown.total)}</strong></div>
              </div>
              <div className="v2-legend-list">
                {breakdown.items.map((e) => (
                  <div key={e.category} className="v2-legend-item">
                    <i className="v2-key" style={{ background: e.color }} />
                    <span className="v2-legend-name">{e.category}</span>
                    <span className="v2-legend-pct">{Math.round(e.pct)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </article>
    ),
    payroll: (
        <article id="v2h-w-payroll" className="v2-card v2h-third">
          <CardHead glyph="people" tint="green" title="Payroll" menu={[{ label: "Go to payroll", glyph: "cash", to: "/dashboard/staff/payroll" }, { label: "Staff", glyph: "user", to: "/dashboard/staff" }]} />
          {first ? <Skel h={180} r={16} /> : payroll.total === 0 ? (
            <div className="v2-empty">No staff records yet.</div>
          ) : (
            <>
              <div className="v2-payroll-top">
                <Ring value={(payroll.paid / (payroll.total || 1)) * 100} size={92} stroke={9} color="var(--v2-good)" label={`${payroll.paid} of ${payroll.total} paid`}>
                  <strong>{payroll.paid}/{payroll.total}</strong><span>paid</span>
                </Ring>
                <div className="v2-payroll-rows">
                  <div><span className="v2-label">Total</span><b>{inr(payroll.paidAmount + payroll.pendingAmount)}</b></div>
                  <div><span className="v2-label">Paid</span><b>{inr(payroll.paidAmount)}</b></div>
                  <div><span className="v2-label">Pending</span><b>{inr(payroll.pendingAmount)}</b></div>
                </div>
              </div>
              {payroll.paid === payroll.total && <Status tone="good">Everyone's been paid</Status>}
            </>
          )}
        </article>
    ),
    attn: (
        <article id="v2h-w-attn" className="v2-card v2h-attn">
          <CardHead glyph="alert" tint="yellow" title="Needs attention"
            extra={
              <Segmented size="sm" ariaLabel="Attention list" value={tab} onChange={setTab} options={[
                { value: "suppliers", label: `Suppliers${supplierDues.length ? ` · ${supplierDues.length}` : ""}` },
                { value: "stock", label: `Stock${lowStock.length ? ` · ${lowStock.length}` : ""}` },
                { value: "recurring", label: `Recurring${recurring.length ? ` · ${recurring.length}` : ""}` },
              ]} />
            } />
          {first ? <div className="v2-list">{[0, 1, 2].map((i) => <Skel key={i} h={46} r={14} />)}</div> : (
            <div className="v2-list v2-scroll v2-tabpanel" key={tab}>
              {tab === "suppliers" && (supplierDues.length === 0
                ? <div className="v2-empty v2-empty--good"><Glyph name="check" size={22} stroke={2} /> No supplier payments pending.</div>
                : supplierDues.map((p) => (
                  <button key={p._id} type="button" className="v2-row" onClick={() => navigate("/dashboard/purchase-entry")}>
                    <Avatar name={p.supplierName} />
                    <span className="v2-row-main">
                      <span className="v2-row-title">{p.supplierName || "Unknown supplier"}</span>
                      <span className="v2-row-sub">{p.purchaseNumber} · {daysSince(p.purchaseDate)}d</span>
                    </span>
                    <span className="v2-row-end"><span className="v2-row-amount">{inr(p.balanceDue)}</span></span>
                  </button>
                )))}
              {tab === "stock" && (lowStock.length === 0
                ? <div className="v2-empty v2-empty--good"><Glyph name="check" size={22} stroke={2} /> Stock levels are healthy.</div>
                : lowStock.map((p) => {
                  const tone = p.currentStock === 0 || p.currentStock <= p.reorderLevel / 2 ? "bad" : "warn";
                  return (
                    <button key={p._id} type="button" className="v2-row" onClick={() => navigate("/dashboard/managestock")}>
                      <WidgetIcon name="box" glyph="box" tint="yellow" size={34} />
                      <span className="v2-row-main">
                        <span className="v2-row-title">{p.name}</span>
                        <span className="v2-row-sub">{p.currentStock} of {p.reorderLevel} reorder level</span>
                      </span>
                      <span className="v2-row-end"><Status tone={tone}>{p.currentStock === 0 ? "Out of stock" : tone === "bad" ? "Critical" : "Low"}</Status></span>
                    </button>
                  );
                }))}
              {tab === "recurring" && (recurring.length === 0
                ? <div className="v2-empty">No recurring invoices due this week.</div>
                : recurring.map((s, i) => (
                  <button key={s._id || i} type="button" className="v2-row" onClick={() => navigate("/dashboard/recurring-invoices")}>
                    <WidgetIcon name="repeat" glyph="repeat" tint="blue" size={34} />
                    <span className="v2-row-main">
                      <span className="v2-row-title">{s.clientName || s.clientId?.clientName || "—"}</span>
                      <span className="v2-row-sub" style={{ textTransform: "capitalize" }}>{s.frequency}</span>
                    </span>
                    <span className="v2-row-end"><span className="v2-row-sub">
                      {s.nextRunDate ? new Date(s.nextRunDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                    </span></span>
                  </button>
                )))}
            </div>
          )}
        </article>
    ),
    activity: (
        <article id="v2h-w-activity" className="v2-card v2h-activity">
          <CardHead glyph="clock" tint="graphite" title="Recent activity" />
          {first ? <div className="v2-list">{[0, 1, 2, 3].map((i) => <Skel key={i} h={40} r={12} />)}</div>
            : activity.length === 0 ? <div className="v2-empty">Nothing yet today.</div> : (
              <ol className="v2-timeline v2-scroll">
                {activity.map((a) => {
                  const map = { invoice: ["invoice", "blue"], client: ["clients", "teal"], expense: ["wallet", "orange"], stock: ["box", "yellow"], payment: ["cash", "green"] };
                  const [g, t] = map[a.type] || ["sparkle", "graphite"];
                  return (
                    <li key={a._id}>
                      <WidgetIcon name={DUO_FOR[g] || g} glyph={g} tint={t} size={28} />
                      <div>
                        <div className="v2-row-title v2-wrap">{a.message}</div>
                        <div className="v2-row-sub">{relTime(a.createdAt)}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
        </article>
    ),
    todo: (
        <article id="v2h-w-todo" className="v2-card v2h-todo">
          <CardHead glyph="alert" tint="red" title="To do & deadlines"
            extra={brief.todo.length > 0 && <span className="v2-count">{brief.todo.length}</span>} />
          {!brief.loaded ? <div className="v2-list">{[0, 1, 2].map((i) => <Skel key={i} h={52} r={14} />)}</div>
            : brief.todo.length === 0 ? <div className="v2-empty v2-empty--good"><Glyph name="check" size={22} stroke={2} /> Nothing overdue or due soon.</div>
            : <div className="v2-list v2-scroll">{brief.todo.map((t) => (
              <button key={t.id} type="button" className={`v2-row v2db-item v2db-item--${t.tone}`} onClick={() => navigate(t.to)}>
                <span className="v2db-item-dot"><Glyph name={t.glyph} size={17} /></span>
                <span className="v2-row-main"><span className="v2-row-title">{t.title}</span><span className="v2-row-sub">{t.sub}</span></span>
                {(t.amount != null || t.amountNote) && <span className="v2db-item-amt">{t.amount != null ? inr(t.amount) : ""}{t.amountNote && <em>{t.amountNote}</em>}</span>}
                <Glyph name="chevronRight" size={16} stroke={2} className="v2db-chev" />
              </button>))}</div>}
        </article>
    ),
    gst: brief.gst ? (
        <article id="v2h-w-gst" className="v2-card v2h-gstw">
          <CardHead glyph="gst" tint="purple" title={`GST for ${brief.prevName}`} menu={[{ label: "GST reports", glyph: "gst", to: "/dashboard/gst-reports" }]} />
          <div className="v2db-gst-big"><span>Net payable</span><strong>{inr(Math.max(0, brief.gst.netPayable?.total || 0), { decimals: 2 })}</strong></div>
          <div className="v2db-gst-eq">
            <div><span>Output tax</span><b>{inr(brief.gst.outward?.totalTax)}</b></div><i>−</i>
            <div><span>Input credit</span><b>{inr(brief.gst.itc?.totalTax)}</b></div>
          </div>
          <div className="v2db-gst-dates">
            {[["GSTR-1", brief.dueDates.gstr1], ["GSTR-3B", brief.dueDates.gstr3b]].map(([n, dt]) => { const left = daysUntil(dt); return (
              <span key={n} className={left < 0 ? "is-past" : left <= 3 ? "is-soon" : ""}><b>{n}</b>{left < 0 ? `was due ${dt.getDate()} ${MONTHS[dt.getMonth()].slice(0, 3)}` : left === 0 ? "due today" : `due in ${left}d`}</span>); })}
          </div>
          <p className="v2-card-foot">Standard monthly due dates — quarterly (QRMP) filers differ.</p>
        </article>
    ) : null,
    owed: brief.owed ? (
        <article id="v2h-w-owed" className="v2-card v2h-owed">
          <CardHead glyph="inbox" tint="indigo" title="Who owes you"
            extra={<span className="v2-card-caption">{inr(brief.owed.total)} · {brief.owed.clients.length} client{brief.owed.clients.length === 1 ? "" : "s"}</span>}
            menu={[{ label: "Client ledger", glyph: "ledger", to: "/dashboard/ledgerpage" }, { label: "Receivables report", glyph: "pie", to: "/dashboard/financial-reports" }]} />
          {brief.owed.clients.length === 0 ? <div className="v2-empty v2-empty--good"><Glyph name="check" size={22} stroke={2} /> Every invoice is paid.</div> : <>
            {brief.owed.aging && <AgingBar total={brief.owed.total} buckets={[{ label: "0–30 days", value: brief.owed.aging.current || 0 }, { label: "31–60", value: brief.owed.aging.days30_60 || 0 }, { label: "61–90", value: brief.owed.aging.days60_90 || 0 }, { label: "90+ days", value: brief.owed.aging.days90plus || 0 }]} />}
            <ol className="v2db-debtors">
              {brief.owed.clients.slice(0, 5).map((c) => (
                <li key={c.name}>
                  <Avatar name={c.name} />
                  <span className="v2-row-main"><span className="v2-row-title">{c.name}</span><span className="v2-row-sub">{c.count} open · oldest {c.oldest} days</span></span>
                  <span className={`v2rp-days${c.oldest > 90 ? " is-bad" : c.oldest > 30 ? " is-warn" : ""}`}>{c.oldest}d</span>
                  <b className="v2db-debtor-amt">{inr(c.total)}</b>
                  <button type="button" className="v2-btn v2-btn--sm" disabled={reminding === c.name} onClick={() => remind(c)}><Glyph name="phone" size={14} /> {reminding === c.name ? "Opening…" : "Remind"}</button>
                </li>))}
            </ol>
            {brief.owed.partial && <p className="v2-card-foot">Showing your 15 oldest unpaid invoices.</p>}
          </>}
        </article>
    ) : null,
  };
  if (KPI_TILES.every((t) => !kpiOn(t.id))) W.kpis = null;

  return (
    <div className="v2h" data-loading={loading && data ? "1" : "0"}>
      {/* ── Greeting + period ─────────────────────────────────────────────── */}
      <header className="v2h-head">
        <div>
          <h1 className="v2h-title">{greeting()}{firstName ? `, ${firstName}` : ""} <span className="v2h-wave" aria-hidden="true">👋</span></h1>
          <p className="v2h-sub">
            {company ? <>Here's how <strong>{company}</strong> is doing</> : "Here's how your business is doing"}
            {mode === "monthly" ? ` in ${MONTHS[month - 1]}.` : ` in ${year}.`}
          </p>
          {brief.loaded && (brief.todo.length > 0 || brief.owed?.over90 > 0) && (
            <div className="v2h-brief">
              {brief.todo.length > 0 && <button type="button" className={`v2h-brief-pill${brief.todo.some((t) => t.tone === "bad") ? " is-bad" : ""}`} onClick={() => goWidget("todo")}>
                <Glyph name="alert" size={14} stroke={2} /> {brief.todo.length} thing{brief.todo.length === 1 ? "" : "s"} to do</button>}
              {brief.owed?.over90 > 0 && <button type="button" className="v2h-brief-pill" onClick={() => goWidget("owed")}>
                <Glyph name="clock" size={14} stroke={2} /> {inr(brief.owed.over90)} owed for 90+ days</button>}
            </div>
          )}
        </div>
        <div className="v2h-controls">
          <Segmented ariaLabel="Period" value={mode} onChange={setMode}
            options={[{ value: "monthly", label: "Month" }, { value: "yearly", label: "Year" }]} />
          <div className="v2-stepper v2-glass" role="group" aria-label="Choose period">
            <button type="button" aria-label="Previous period" onClick={() => step(-1)}><Glyph name="chevronLeft" size={16} stroke={2} /></button>
            <span className="v2-stepper-label">{periodLabel}</span>
            <button type="button" aria-label="Next period" disabled={atLatest} onClick={() => step(1)}><Glyph name="chevronRight" size={16} stroke={2} /></button>
          </div>
          {!isPhone && (
            <MenuButton className="v2-btn v2-btn--primary" label="Create new" width={230}
              header={<div className="v2-menu-caption">Create new</div>}
              items={createActions.map((a) => ({ label: a.label, glyph: a.glyph, to: a.to }))}>
              <Glyph name="plus" size={17} stroke={2.2} /> New
            </MenuButton>
          )}
          <button type="button" className="v2-icon-btn v2-glass" aria-label="Customise dashboard" title="Customise dashboard" onClick={() => setCustomising(true)}>
            <Glyph name="settings" size={18} />
          </button>
        </div>
      </header>

      {error && (
        <div className="v2-banner v2-banner--bad" role="alert">
          <Glyph name="alert" size={18} /> {error}
          <button type="button" className="v2-btn v2-btn--sm" onClick={fetchData}>Try again</button>
        </div>
      )}

      <div className="v2h-grid">
        {layout.order.filter((id) => !layout.hidden.includes(id) && W[id]).map((id) => <React.Fragment key={id}>{W[id]}</React.Fragment>)}
      </div>

      <RecordPaymentSheet invoice={payFor} originRef={payOrigin} onClose={() => setPayFor(null)}
        onDone={(msg) => { setPayFor(null); fetchData(); showAlert?.(msg, "success"); }} />

      <Sheet open={customising} onClose={() => setCustomising(false)} width={500} title="Customise dashboard" subtitle="Show, hide and reorder widgets · saved on this device"
        footer={<><button type="button" className="v2-btn" onClick={() => setLayout(DEFAULT_LAYOUT)}>Reset</button><button type="button" className="v2-btn v2-btn--primary" onClick={() => setCustomising(false)}>Done</button></>}>
        <ol className="v2db-custom">
          {layout.order.map((id, i) => {
            const w = WIDGETS.find((x) => x.id === id);
            const move = (dir) => setLayout((l) => { const o = [...l.order]; [o[i + dir], o[i]] = [o[i], o[i + dir]]; return { ...l, order: o }; });
            return (
              <li key={id} className={layout.hidden.includes(id) ? "is-off" : ""}>
                <div className="v2db-custom-main">
                  <Switch id={`v2h-show-${id}`} checked={!layout.hidden.includes(id)} label={<>{w.label}{w.isNew && <span className="v2db-new">New</span>}</>}
                    onChange={(on) => setLayout((l) => ({ ...l, hidden: on ? l.hidden.filter((x) => x !== id) : [...l.hidden, id] }))} />
                  {id === "kpis" && !layout.hidden.includes("kpis") && (
                    <div className="v2db-custom-kpis">{KPI_TILES.map((t) => (
                      <button key={t.id} type="button" className={`v2-chip-btn v2i-preset${kpiOn(t.id) ? " is-on" : ""}`} aria-pressed={kpiOn(t.id)}
                        onClick={() => setLayout((l) => ({ ...l, kpiHidden: kpiOn(t.id) ? [...l.kpiHidden, t.id] : l.kpiHidden.filter((x) => x !== t.id) }))}>{t.label}</button>))}</div>)}
                </div>
                <span className="v2db-custom-move">
                  <button type="button" className="v2-icon-btn" aria-label={`Move ${w.label} up`} disabled={i === 0} onClick={() => move(-1)}><Glyph name="chevronDown" size={15} stroke={2} style={{ transform: "rotate(180deg)" }} /></button>
                  <button type="button" className="v2-icon-btn" aria-label={`Move ${w.label} down`} disabled={i === layout.order.length - 1} onClick={() => move(1)}><Glyph name="chevronDown" size={15} stroke={2} /></button>
                </span>
              </li>);
          })}
        </ol>
      </Sheet>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Record payment — same request/validation as v1's modal; the TDS confirmation
// is now a live preview + an explicit button label instead of window.confirm.
// ══════════════════════════════════════════════════════════════════════════════
export function RecordPaymentSheet({ invoice, originRef, onClose, onDone }) {
  const today = new Date().toISOString().split("T")[0];
  const [amount, setAmount] = useState("");
  const [full, setFull] = useState(false);
  const [date, setDate] = useState(today);
  const [modeP, setModeP] = useState("Cash");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keep = useRef(invoice);
  if (invoice) keep.current = invoice; // keep content during the exit animation
  const inv = keep.current;

  useEffect(() => {
    if (invoice) { setAmount(""); setFull(false); setDate(today); setModeP("Cash"); setReference(""); setNote(""); setErr(""); }
  }, [invoice]); // eslint-disable-line

  if (!inv) return null;
  const due = (inv.grandTotal || 0) - (inv.amountPaid || 0);
  const amt = parseFloat(amount);
  const tds = full && amt > 0 && amt < due ? due - amt : 0;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!amt || amt <= 0) { setErr("Enter the amount you received."); return; }
    if (!full && amt > due) { setErr(`That's more than the balance due (${inr(due, { decimals: 2 })}).`); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/invoices/payment/${inv._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "auth-token": localStorage.getItem("token") },
        body: JSON.stringify({ amount: amt, paymentDate: date, paymentMode: modeP, reference, note, isFullPayment: full }),
      });
      const result = await res.json();
      if (!res.ok) { setErr(result.error || "Couldn't record the payment."); return; }
      onDone(result.tdsDeducted ? `Payment recorded · TDS ${inr(result.tdsAmount)} noted` : "Payment recorded");
    } catch {
      setErr("Network error — the payment wasn't recorded.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!invoice} onClose={onClose} originRef={originRef} title="Record payment"
      subtitle={`${inv.invoiceNumber} · ${inv.clientName}`}
      footer={
        <>
          <button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="v2-pay-form" className="v2-btn v2-btn--primary" disabled={busy}>
            {busy ? "Saving…" : tds > 0 ? `Record + ${inr(tds)} TDS` : "Record payment"}
          </button>
        </>
      }>
      <form id="v2-pay-form" onSubmit={submit} className="v2-form">
        <div className="v2-inset v2-summary">
          <div className="v2-inset-row v2-inset-row--static"><span>Invoice total</span><b>{inr(inv.grandTotal, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>Already paid</span><b>{inr(inv.amountPaid, { decimals: 2 })}</b></div>
          <div className="v2-inset-row v2-inset-row--static"><span>Balance due</span><b className="v2-accent-text">{inr(due, { decimals: 2 })}</b></div>
        </div>

        <label className="v2-field">
          <span className="v2-field-label">Amount received</span>
          <span className="v2-input-wrap v2-input-wrap--money">
            <span className="v2-input-prefix">₹</span>
            <input className="v2-input v2-input--lg" type="number" step="0.01" inputMode="decimal" required
              value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            <button type="button" className="v2-chip-btn" onClick={() => setAmount(String(due))}>Full due</button>
          </span>
        </label>

        <div className="v2-inset">
          <Switch id="v2-full" checked={full} onChange={setFull} label="This settles the invoice"
            description="Client deducted TDS — record the shortfall as TDS" />
        </div>
        {tds > 0 && (
          <div className="v2-banner v2-banner--warn" role="status">
            <Glyph name="percent" size={17} /> {inr(tds, { decimals: 2 })} will be recorded as TDS receivable.
          </div>
        )}

        <div className="v2-form-row">
          <label className="v2-field">
            <span className="v2-field-label">Date</span>
            <input className="v2-input" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="v2-field">
            <span className="v2-field-label">Mode</span>
            <select className="v2-input" required value={modeP} onChange={(e) => setModeP(e.target.value)}>
              {["Cash", "Bank Transfer", "UPI", "Cheque", "Card", "Other"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
        </div>
        <label className="v2-field">
          <span className="v2-field-label">Reference <span className="v2-optional">optional</span></span>
          <input className="v2-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR, cheque no., transaction ID" />
        </label>
        <label className="v2-field">
          <span className="v2-field-label">Note <span className="v2-optional">optional</span></span>
          <textarea className="v2-input v2-textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {err && <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={17} /> {err}</div>}
      </form>
    </Sheet>
  );
}
